// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';

export function isLayoutModel(source: any): source is ILayout {
    // Method Implementation: simple
    return source.modelName === 'Layout';
}

export function isGroupModel(source: any): source is IGroup {
    // Method Implementation: simple
    return source.modelName === 'Group';
}

export function isQuestionModel(source: any): source is IQuestion {
    // Method Implementation: simple
    return source.modelName === 'Question';
}

export function isAnswerModel(source: any): source is IAnswer {
    // Method Implementation: simple
    return source.modelName === 'Answer';
}

export function isLinkedLayoutModel(source: any): source is ILinkedLayout {
    // Method Implementation: simple
    return source.modelName === 'LinkedLayout';
}

export function isInlineLinkedLayoutModel(source: any): source is IInlineLinkedLayout {
    // Method Implementation: simple
    return source.modelName === 'InlineLinkedLayout';
}

export function isReferenceLinkedLayoutModel(source: any): source is IReferenceLinkedLayout {
    // Method Implementation: simple
    return source.modelName === 'ReferenceLinkedLayout';
}

export interface LayoutZodSchemaShape {
    name?: string;

    groups?: GroupZodSchemaShape[];

    layouts?: LinkedLayoutZodSchemaShape[];
}

export interface GroupZodSchemaShape {
    name?: string;

    displayKey?: string;

    descriptionKey?: string;

    visible?: string;

    enabled?: string;

    groups?: GroupZodSchemaShape[];

    questions?: QuestionZodSchemaShape[];
}

export interface QuestionZodSchemaShape {
    name?: string;

    source?: string;

    optional?: boolean;

    displayKey?: string;

    descriptionKey?: string;

    visible?: string;

    enabled?: string;

    answers?: AnswerZodSchemaShape[];
}

export interface AnswerZodSchemaShape {
    name?: string;

    optional?: boolean;

    visible?: string;

    enabled?: string;

    displayKey?: string;
}

export interface LinkedLayoutZodSchemaShape {
    linkedLayoutType?: string;

    name?: string;
}

export interface InlineLinkedLayoutZodSchemaShape {
    linkedLayoutType?: string;

    name?: string;

    layout?: LayoutZodSchemaShape;
}

export interface ReferenceLinkedLayoutZodSchemaShape {
    linkedLayoutType?: string;

    name?: string;

    layout?: string;
}

// Artifact Implementation: artifacts/zodSchemaType
export const LayoutZodSchema: Zod.ZodSchema<LayoutZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    groups: Zod.array(GroupZodSchema).optional(),
    layouts: Zod.array(LinkedLayoutZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const GroupZodSchema: Zod.ZodSchema<GroupZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    descriptionKey: Zod.string().optional(),
    visible: Zod.string().optional(),
    enabled: Zod.string().optional(),
    groups: Zod.array(GroupZodSchema).optional(),
    questions: Zod.array(QuestionZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const QuestionZodSchema: Zod.ZodSchema<QuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    optional: Zod.boolean().optional(),
    displayKey: Zod.string().optional(),
    descriptionKey: Zod.string().optional(),
    visible: Zod.string().optional(),
    enabled: Zod.string().optional(),
    answers: Zod.array(AnswerZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const AnswerZodSchema: Zod.ZodSchema<AnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    optional: Zod.boolean().optional(),
    visible: Zod.string().optional(),
    enabled: Zod.string().optional(),
    displayKey: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const LinkedLayoutZodSchema: Zod.ZodSchema<LinkedLayoutZodSchemaShape> = Zod.lazy(() => Zod.object({
    linkedLayoutType: Zod.string(),
    name: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const InlineLinkedLayoutZodSchema: Zod.ZodSchema<InlineLinkedLayoutZodSchemaShape> = Zod.lazy(() => Zod.object({
    linkedLayoutType: Zod.string(),
    name: Zod.string().optional(),
    layout: LayoutZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ReferenceLinkedLayoutZodSchema: Zod.ZodSchema<ReferenceLinkedLayoutZodSchemaShape> = Zod.lazy(() => Zod.object({
    linkedLayoutType: Zod.string(),
    name: Zod.string().optional(),
    layout: Zod.string().optional()
}).nonstrict());

export interface ILayout extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly groups: Group[];

    readonly layouts: ALinkedLayout[];

    readonly isNameDefined: boolean;

    readonly isGroupsDefined: boolean;

    readonly isLayoutsDefined: boolean;
}

export interface IGroup extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly displayKey: string;

    readonly descriptionKey: string;

    readonly visible: string;

    readonly enabled: string;

    readonly groups: Group[];

    readonly questions: Question[];

    readonly isNameDefined: boolean;

    readonly isDisplayKeyDefined: boolean;

    readonly isDescriptionKeyDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isGroupsDefined: boolean;

    readonly isQuestionsDefined: boolean;
}

export interface IQuestion extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly source: string;

    readonly optional: boolean;

    readonly displayKey: string;

    readonly descriptionKey: string;

    readonly visible: string;

    readonly enabled: string;

    readonly answers: Answer[];

    readonly isNameDefined: boolean;

    readonly isSourceDefined: boolean;

    readonly isOptionalDefined: boolean;

    readonly isDisplayKeyDefined: boolean;

    readonly isDescriptionKeyDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isAnswersDefined: boolean;
}

export interface IAnswer extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly optional: boolean;

    readonly visible: string;

    readonly enabled: string;

    readonly displayKey: string;

    readonly isNameDefined: boolean;

    readonly isOptionalDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isDisplayKeyDefined: boolean;
}

export interface ILinkedLayout extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly isNameDefined: boolean;
}

export interface IInlineLinkedLayout extends ILinkedLayout, Wellspring.IModel {
    readonly modelName: string;

    readonly layout: Layout;

    readonly isLayoutDefined: boolean;
}

export interface IReferenceLinkedLayout extends ILinkedLayout, Wellspring.IModel {
    readonly modelName: string;

    readonly layout: string;

    readonly isLayoutDefined: boolean;
}

export class Layout implements ILayout {
    public static readonly modelName: string = 'Layout';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'groups', 'layouts' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Layout.modelName;
    }

    private _name: string;

    private _groups: Group[];

    private _layouts: ALinkedLayout[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get groups(): Group[] {
        // Method Implementation: simple
        return this._groups;
    }

    public get layouts(): ALinkedLayout[] {
        // Method Implementation: simple
        return this._layouts;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set groups(value: Group[]) {
        // Method Implementation: simple
        this._groups = value;
        this._isGroupsDefined = true;
    }

    public set layouts(value: ALinkedLayout[]) {
        // Method Implementation: simple
        this._layouts = value;
        this._isLayoutsDefined = true;
    }

    private _isNameDefined: boolean;

    private _isGroupsDefined: boolean;

    private _isLayoutsDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isGroupsDefined(): boolean {
        // Method Implementation: simple
        return this._isGroupsDefined;
    }

    public get isLayoutsDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutsDefined;
    }

    public constructor();
    public constructor(source: ILayout);
    public constructor(source: Partial<Pick<Layout, 'name' | 'groups' | 'layouts'>>);
    public constructor(source?: ILayout | Partial<Pick<Layout, 'name' | 'groups' | 'layouts'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isLayoutModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isGroupsDefined) {
                    this._groups = source.groups;
                    this._isGroupsDefined = true;
                }
                else {
                    this._groups = [];
                    this._isGroupsDefined = true;
                }
                
                if (source.isLayoutsDefined) {
                    this._layouts = source.layouts;
                    this._isLayoutsDefined = true;
                }
                else {
                    this._layouts = [];
                    this._isLayoutsDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.groups !== undefined) {
                    this._groups = source.groups;
                    this._isGroupsDefined = true;
                }
                else {
                    this._groups = [];
                    this._isGroupsDefined = true;
                }
                
                if (source.layouts !== undefined) {
                    this._layouts = source.layouts;
                    this._isLayoutsDefined = true;
                }
                else {
                    this._layouts = [];
                    this._isLayoutsDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._groups = [];
            this._isGroupsDefined = true;
        
            this._layouts = [];
            this._isLayoutsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Layout.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isGroupsDefined && this.groups !== undefined)
            definedProperties.push('groups');
        if (this.isLayoutsDefined && this.layouts !== undefined)
            definedProperties.push('layouts');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'groups':
                return this.groups;
            case 'layouts':
                return this.layouts;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isGroupsDefined && this.groups !== undefined) {
            if (this.groups !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groups', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.groups.length));
                for (let i = 0; i < this.groups.length; i++) {
                    const item = this.groups[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groups', 'null'));
            }
        }
        
        if (this.isLayoutsDefined && this.layouts !== undefined) {
            if (this.layouts !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layouts', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.layouts.length));
                for (let i = 0; i < this.layouts.length; i++) {
                    const item = this.layouts[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layouts', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'groups':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.groups = [];
                
                    let item: Group = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Group.modelName) {
                                item = new Group();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Group.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'groups' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'groups'`);
                        }
                
                        this.groups.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'groups' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'groups'`);
                }
        
                return true;
            case 'layouts':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.layouts = [];
                
                    let item: ALinkedLayout = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'layouts' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'layouts'`);
                        }
                
                        this.layouts.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'layouts' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layouts'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // groups
        if (this.isGroupsDefined && this.groups !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.groups === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.groups.length);
            
                for (const item of this.groups) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // layouts
        if (this.isLayoutsDefined && this.layouts !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.layouts === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.layouts.length);
            
                for (const item of this.layouts) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof InlineLinkedLayout)
                            writer.writeShortValue(0);
                        else if (item instanceof ReferenceLinkedLayout)
                            writer.writeShortValue(1);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // groups
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Group;
            
                    this.groups = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Group();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.groups.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // layouts
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ALinkedLayout;
            
                    this.layouts = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new InlineLinkedLayout();
                                        break;
                                    case 1:
                                        item = new ReferenceLinkedLayout();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.layouts.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Layout {
        // Method Implementation: simple
        const schema = LayoutZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: LayoutZodSchemaShape): Layout {
        // Method Implementation: parseZodSchema
        return new Layout({
            name: schema.name,
            groups: schema.groups?.map(i => Group.parseZodSchema(i)),
            layouts: schema.layouts?.map(i => ALinkedLayout.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            groups: this.isGroupsDefined ? (this.groups?.map(item => item)) : undefined,
            layouts: this.isLayoutsDefined ? (this.layouts?.map(item => item)) : undefined,
        };
    }

    public clone(): Layout {
        // Method Implementation: clone
        return new Layout({
            name: this.isNameDefined ? this.name : undefined,
            groups: this.isGroupsDefined ? this.groups?.map(i => i.clone()) : undefined,
            layouts: this.isLayoutsDefined ? this.layouts?.map(i => i.clone()) : undefined
        });
    }
}

export class Group implements IGroup {
    public static readonly modelName: string = 'Group';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'displayKey', 'descriptionKey', 'visible', 'enabled', 'groups', 'questions' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Group.modelName;
    }

    private _name: string;

    private _displayKey: string;

    private _descriptionKey: string;

    private _visible: string;

    private _enabled: string;

    private _groups: Group[];

    private _questions: Question[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get displayKey(): string {
        // Method Implementation: simple
        return this._displayKey;
    }

    public get descriptionKey(): string {
        // Method Implementation: simple
        return this._descriptionKey;
    }

    public get visible(): string {
        // Method Implementation: simple
        return this._visible;
    }

    public get enabled(): string {
        // Method Implementation: simple
        return this._enabled;
    }

    public get groups(): Group[] {
        // Method Implementation: simple
        return this._groups;
    }

    public get questions(): Question[] {
        // Method Implementation: simple
        return this._questions;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set displayKey(value: string) {
        // Method Implementation: simple
        this._displayKey = value;
        this._isDisplayKeyDefined = true;
    }

    public set descriptionKey(value: string) {
        // Method Implementation: simple
        this._descriptionKey = value;
        this._isDescriptionKeyDefined = true;
    }

    public set visible(value: string) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set enabled(value: string) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set groups(value: Group[]) {
        // Method Implementation: simple
        this._groups = value;
        this._isGroupsDefined = true;
    }

    public set questions(value: Question[]) {
        // Method Implementation: simple
        this._questions = value;
        this._isQuestionsDefined = true;
    }

    private _isNameDefined: boolean;

    private _isDisplayKeyDefined: boolean;

    private _isDescriptionKeyDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isGroupsDefined: boolean;

    private _isQuestionsDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isDisplayKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayKeyDefined;
    }

    public get isDescriptionKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isDescriptionKeyDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isGroupsDefined(): boolean {
        // Method Implementation: simple
        return this._isGroupsDefined;
    }

    public get isQuestionsDefined(): boolean {
        // Method Implementation: simple
        return this._isQuestionsDefined;
    }

    public constructor();
    public constructor(source: IGroup);
    public constructor(source: Partial<Pick<Group, 'name' | 'displayKey' | 'descriptionKey' | 'visible' | 'enabled' | 'groups' | 'questions'>>);
    public constructor(source?: IGroup | Partial<Pick<Group, 'name' | 'displayKey' | 'descriptionKey' | 'visible' | 'enabled' | 'groups' | 'questions'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isGroupModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isDisplayKeyDefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
                
                if (source.isDescriptionKeyDefined) {
                    this._descriptionKey = source.descriptionKey;
                    this._isDescriptionKeyDefined = true;
                }
                else {
                    this._descriptionKey = '';
                    this._isDescriptionKeyDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = '';
                    this._isVisibleDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
                
                if (source.isGroupsDefined) {
                    this._groups = source.groups;
                    this._isGroupsDefined = true;
                }
                else {
                    this._groups = [];
                    this._isGroupsDefined = true;
                }
                
                if (source.isQuestionsDefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.displayKey !== undefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
                
                if (source.descriptionKey !== undefined) {
                    this._descriptionKey = source.descriptionKey;
                    this._isDescriptionKeyDefined = true;
                }
                else {
                    this._descriptionKey = '';
                    this._isDescriptionKeyDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = '';
                    this._isVisibleDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
                
                if (source.groups !== undefined) {
                    this._groups = source.groups;
                    this._isGroupsDefined = true;
                }
                else {
                    this._groups = [];
                    this._isGroupsDefined = true;
                }
                
                if (source.questions !== undefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._displayKey = '';
            this._isDisplayKeyDefined = false;
        
            this._descriptionKey = '';
            this._isDescriptionKeyDefined = false;
        
            this._visible = '';
            this._isVisibleDefined = false;
        
            this._enabled = '';
            this._isEnabledDefined = false;
        
            this._groups = [];
            this._isGroupsDefined = true;
        
            this._questions = [];
            this._isQuestionsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Group.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isDisplayKeyDefined && this.displayKey !== undefined)
            definedProperties.push('displayKey');
        if (this.isDescriptionKeyDefined && this.descriptionKey !== undefined)
            definedProperties.push('descriptionKey');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isGroupsDefined && this.groups !== undefined)
            definedProperties.push('groups');
        if (this.isQuestionsDefined && this.questions !== undefined)
            definedProperties.push('questions');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'displayKey':
                return this.displayKey;
            case 'descriptionKey':
                return this.descriptionKey;
            case 'visible':
                return this.visible;
            case 'enabled':
                return this.enabled;
            case 'groups':
                return this.groups;
            case 'questions':
                return this.questions;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            if (this.displayKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'null'));
            }
        }
        
        if (this.isDescriptionKeyDefined && this.descriptionKey !== undefined) {
            if (this.descriptionKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('descriptionKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.descriptionKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('descriptionKey', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isGroupsDefined && this.groups !== undefined) {
            if (this.groups !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groups', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.groups.length));
                for (let i = 0; i < this.groups.length; i++) {
                    const item = this.groups[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groups', 'null'));
            }
        }
        
        if (this.isQuestionsDefined && this.questions !== undefined) {
            if (this.questions !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.questions.length));
                for (let i = 0; i < this.questions.length; i++) {
                    const item = this.questions[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'displayKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayKey' cannot be null`);
                    this.displayKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayKey'`);
                }
        
                return true;
            case 'descriptionKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.descriptionKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'descriptionKey' cannot be null`);
                    this.descriptionKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'descriptionKey'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.visible = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'visible' cannot be null`);
                    this.visible = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.enabled = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'enabled' cannot be null`);
                    this.enabled = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'groups':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.groups = [];
                
                    let item: Group = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Group.modelName) {
                                item = new Group();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Group.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'groups' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'groups'`);
                        }
                
                        this.groups.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'groups' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'groups'`);
                }
        
                return true;
            case 'questions':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.questions = [];
                
                    let item: Question = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Question.modelName) {
                                item = new Question();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Question.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'questions' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'questions'`);
                        }
                
                        this.questions.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'questions' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'questions'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayKey
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // descriptionKey
        if (this.isDescriptionKeyDefined && this.descriptionKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.descriptionKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.descriptionKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.visible === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.visible);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.enabled === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.enabled);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // groups
        if (this.isGroupsDefined && this.groups !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.groups === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.groups.length);
            
                for (const item of this.groups) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // questions
        if (this.isQuestionsDefined && this.questions !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.questions === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.questions.length);
            
                for (const item of this.questions) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayKey = <any>null;
                    break;
                case 1:
                    this.displayKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // descriptionKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.descriptionKey = <any>null;
                    break;
                case 1:
                    this.descriptionKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.visible = <any>null;
                    break;
                case 1:
                    this.visible = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.enabled = <any>null;
                    break;
                case 1:
                    this.enabled = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // groups
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Group;
            
                    this.groups = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Group();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.groups.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // questions
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Question;
            
                    this.questions = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Question();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.questions.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Group {
        // Method Implementation: simple
        const schema = GroupZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: GroupZodSchemaShape): Group {
        // Method Implementation: parseZodSchema
        return new Group({
            name: schema.name,
            displayKey: schema.displayKey,
            descriptionKey: schema.descriptionKey,
            visible: schema.visible,
            enabled: schema.enabled,
            groups: schema.groups?.map(i => Group.parseZodSchema(i)),
            questions: schema.questions?.map(i => Question.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            displayKey: this.isDisplayKeyDefined ? ((this.displayKey === null ? undefined : this.displayKey)) : undefined,
            descriptionKey: this.isDescriptionKeyDefined ? ((this.descriptionKey === null ? undefined : this.descriptionKey)) : undefined,
            visible: this.isVisibleDefined ? ((this.visible === null ? undefined : this.visible)) : undefined,
            enabled: this.isEnabledDefined ? ((this.enabled === null ? undefined : this.enabled)) : undefined,
            groups: this.isGroupsDefined ? (this.groups?.map(item => item)) : undefined,
            questions: this.isQuestionsDefined ? (this.questions?.map(item => item)) : undefined,
        };
    }

    public clone(): Group {
        // Method Implementation: clone
        return new Group({
            name: this.isNameDefined ? this.name : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            descriptionKey: this.isDescriptionKeyDefined ? this.descriptionKey : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            groups: this.isGroupsDefined ? this.groups?.map(i => i.clone()) : undefined,
            questions: this.isQuestionsDefined ? this.questions?.map(i => i.clone()) : undefined
        });
    }
}

export class Question implements IQuestion {
    public static readonly modelName: string = 'Question';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'source', 'optional', 'displayKey', 'descriptionKey', 'visible', 'enabled', 'answers' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Question.modelName;
    }

    private _name: string;

    private _source: string;

    private _optional: boolean;

    private _displayKey: string;

    private _descriptionKey: string;

    private _visible: string;

    private _enabled: string;

    private _answers: Answer[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get optional(): boolean {
        // Method Implementation: simple
        return this._optional;
    }

    public get displayKey(): string {
        // Method Implementation: simple
        return this._displayKey;
    }

    public get descriptionKey(): string {
        // Method Implementation: simple
        return this._descriptionKey;
    }

    public get visible(): string {
        // Method Implementation: simple
        return this._visible;
    }

    public get enabled(): string {
        // Method Implementation: simple
        return this._enabled;
    }

    public get answers(): Answer[] {
        // Method Implementation: simple
        return this._answers;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set optional(value: boolean) {
        // Method Implementation: simple
        this._optional = value;
        this._isOptionalDefined = true;
    }

    public set displayKey(value: string) {
        // Method Implementation: simple
        this._displayKey = value;
        this._isDisplayKeyDefined = true;
    }

    public set descriptionKey(value: string) {
        // Method Implementation: simple
        this._descriptionKey = value;
        this._isDescriptionKeyDefined = true;
    }

    public set visible(value: string) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set enabled(value: string) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set answers(value: Answer[]) {
        // Method Implementation: simple
        this._answers = value;
        this._isAnswersDefined = true;
    }

    private _isNameDefined: boolean;

    private _isSourceDefined: boolean;

    private _isOptionalDefined: boolean;

    private _isDisplayKeyDefined: boolean;

    private _isDescriptionKeyDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isAnswersDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isOptionalDefined(): boolean {
        // Method Implementation: simple
        return this._isOptionalDefined;
    }

    public get isDisplayKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayKeyDefined;
    }

    public get isDescriptionKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isDescriptionKeyDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isAnswersDefined(): boolean {
        // Method Implementation: simple
        return this._isAnswersDefined;
    }

    public constructor();
    public constructor(source: IQuestion);
    public constructor(source: Partial<Pick<Question, 'name' | 'source' | 'optional' | 'displayKey' | 'descriptionKey' | 'visible' | 'enabled' | 'answers'>>);
    public constructor(source?: IQuestion | Partial<Pick<Question, 'name' | 'source' | 'optional' | 'displayKey' | 'descriptionKey' | 'visible' | 'enabled' | 'answers'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isQuestionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isOptionalDefined) {
                    this._optional = source.optional;
                    this._isOptionalDefined = true;
                }
                else {
                    this._optional = false;
                    this._isOptionalDefined = false;
                }
                
                if (source.isDisplayKeyDefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
                
                if (source.isDescriptionKeyDefined) {
                    this._descriptionKey = source.descriptionKey;
                    this._isDescriptionKeyDefined = true;
                }
                else {
                    this._descriptionKey = '';
                    this._isDescriptionKeyDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = '';
                    this._isVisibleDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
                
                if (source.isAnswersDefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.optional !== undefined) {
                    this._optional = source.optional;
                    this._isOptionalDefined = true;
                }
                else {
                    this._optional = false;
                    this._isOptionalDefined = false;
                }
                
                if (source.displayKey !== undefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
                
                if (source.descriptionKey !== undefined) {
                    this._descriptionKey = source.descriptionKey;
                    this._isDescriptionKeyDefined = true;
                }
                else {
                    this._descriptionKey = '';
                    this._isDescriptionKeyDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = '';
                    this._isVisibleDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
                
                if (source.answers !== undefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._source = '';
            this._isSourceDefined = false;
        
            this._optional = false;
            this._isOptionalDefined = false;
        
            this._displayKey = '';
            this._isDisplayKeyDefined = false;
        
            this._descriptionKey = '';
            this._isDescriptionKeyDefined = false;
        
            this._visible = '';
            this._isVisibleDefined = false;
        
            this._enabled = '';
            this._isEnabledDefined = false;
        
            this._answers = [];
            this._isAnswersDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Question.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isOptionalDefined && this.optional !== undefined)
            definedProperties.push('optional');
        if (this.isDisplayKeyDefined && this.displayKey !== undefined)
            definedProperties.push('displayKey');
        if (this.isDescriptionKeyDefined && this.descriptionKey !== undefined)
            definedProperties.push('descriptionKey');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isAnswersDefined && this.answers !== undefined)
            definedProperties.push('answers');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'source':
                return this.source;
            case 'optional':
                return this.optional;
            case 'displayKey':
                return this.displayKey;
            case 'descriptionKey':
                return this.descriptionKey;
            case 'visible':
                return this.visible;
            case 'enabled':
                return this.enabled;
            case 'answers':
                return this.answers;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isOptionalDefined && this.optional !== undefined) {
            if (this.optional !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('optional', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.optional);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('optional', 'null'));
            }
        }
        
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            if (this.displayKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'null'));
            }
        }
        
        if (this.isDescriptionKeyDefined && this.descriptionKey !== undefined) {
            if (this.descriptionKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('descriptionKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.descriptionKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('descriptionKey', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isAnswersDefined && this.answers !== undefined) {
            if (this.answers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.answers.length));
                for (let i = 0; i < this.answers.length; i++) {
                    const item = this.answers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'optional':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.optional = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'optional' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'optional'`);
                }
        
                return true;
            case 'displayKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayKey' cannot be null`);
                    this.displayKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayKey'`);
                }
        
                return true;
            case 'descriptionKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.descriptionKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'descriptionKey' cannot be null`);
                    this.descriptionKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'descriptionKey'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.visible = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'visible' cannot be null`);
                    this.visible = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.enabled = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'enabled' cannot be null`);
                    this.enabled = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'answers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.answers = [];
                
                    let item: Answer = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Answer.modelName) {
                                item = new Answer();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Answer.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'answers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'answers'`);
                        }
                
                        this.answers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'answers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'answers'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // optional
        if (this.isOptionalDefined && this.optional !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.optional);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayKey
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // descriptionKey
        if (this.isDescriptionKeyDefined && this.descriptionKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.descriptionKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.descriptionKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.visible === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.visible);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.enabled === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.enabled);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // answers
        if (this.isAnswersDefined && this.answers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.answers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.answers.length);
            
                for (const item of this.answers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // optional
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.optional = reader.readBooleanValue();
        }
        
        // displayKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayKey = <any>null;
                    break;
                case 1:
                    this.displayKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // descriptionKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.descriptionKey = <any>null;
                    break;
                case 1:
                    this.descriptionKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.visible = <any>null;
                    break;
                case 1:
                    this.visible = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.enabled = <any>null;
                    break;
                case 1:
                    this.enabled = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // answers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Answer;
            
                    this.answers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Answer();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.answers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Question {
        // Method Implementation: simple
        const schema = QuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: QuestionZodSchemaShape): Question {
        // Method Implementation: parseZodSchema
        return new Question({
            name: schema.name,
            source: schema.source,
            optional: schema.optional,
            displayKey: schema.displayKey,
            descriptionKey: schema.descriptionKey,
            visible: schema.visible,
            enabled: schema.enabled,
            answers: schema.answers?.map(i => Answer.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            optional: this.isOptionalDefined ? (this.optional) : undefined,
            displayKey: this.isDisplayKeyDefined ? ((this.displayKey === null ? undefined : this.displayKey)) : undefined,
            descriptionKey: this.isDescriptionKeyDefined ? ((this.descriptionKey === null ? undefined : this.descriptionKey)) : undefined,
            visible: this.isVisibleDefined ? ((this.visible === null ? undefined : this.visible)) : undefined,
            enabled: this.isEnabledDefined ? ((this.enabled === null ? undefined : this.enabled)) : undefined,
            answers: this.isAnswersDefined ? (this.answers?.map(item => item)) : undefined,
        };
    }

    public clone(): Question {
        // Method Implementation: clone
        return new Question({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            optional: this.isOptionalDefined ? this.optional : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            descriptionKey: this.isDescriptionKeyDefined ? this.descriptionKey : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            answers: this.isAnswersDefined ? this.answers?.map(i => i.clone()) : undefined
        });
    }
}

export class Answer implements IAnswer {
    public static readonly modelName: string = 'Answer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'optional', 'visible', 'enabled', 'displayKey' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Answer.modelName;
    }

    private _name: string;

    private _optional: boolean;

    private _visible: string;

    private _enabled: string;

    private _displayKey: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get optional(): boolean {
        // Method Implementation: simple
        return this._optional;
    }

    public get visible(): string {
        // Method Implementation: simple
        return this._visible;
    }

    public get enabled(): string {
        // Method Implementation: simple
        return this._enabled;
    }

    public get displayKey(): string {
        // Method Implementation: simple
        return this._displayKey;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set optional(value: boolean) {
        // Method Implementation: simple
        this._optional = value;
        this._isOptionalDefined = true;
    }

    public set visible(value: string) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set enabled(value: string) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set displayKey(value: string) {
        // Method Implementation: simple
        this._displayKey = value;
        this._isDisplayKeyDefined = true;
    }

    private _isNameDefined: boolean;

    private _isOptionalDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isDisplayKeyDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isOptionalDefined(): boolean {
        // Method Implementation: simple
        return this._isOptionalDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isDisplayKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayKeyDefined;
    }

    public constructor();
    public constructor(source: IAnswer);
    public constructor(source: Partial<Pick<Answer, 'name' | 'optional' | 'visible' | 'enabled' | 'displayKey'>>);
    public constructor(source?: IAnswer | Partial<Pick<Answer, 'name' | 'optional' | 'visible' | 'enabled' | 'displayKey'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isAnswerModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isOptionalDefined) {
                    this._optional = source.optional;
                    this._isOptionalDefined = true;
                }
                else {
                    this._optional = false;
                    this._isOptionalDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = '';
                    this._isVisibleDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
                
                if (source.isDisplayKeyDefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.optional !== undefined) {
                    this._optional = source.optional;
                    this._isOptionalDefined = true;
                }
                else {
                    this._optional = false;
                    this._isOptionalDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = '';
                    this._isVisibleDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
                
                if (source.displayKey !== undefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._optional = false;
            this._isOptionalDefined = false;
        
            this._visible = '';
            this._isVisibleDefined = false;
        
            this._enabled = '';
            this._isEnabledDefined = false;
        
            this._displayKey = '';
            this._isDisplayKeyDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Answer.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isOptionalDefined && this.optional !== undefined)
            definedProperties.push('optional');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isDisplayKeyDefined && this.displayKey !== undefined)
            definedProperties.push('displayKey');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'optional':
                return this.optional;
            case 'visible':
                return this.visible;
            case 'enabled':
                return this.enabled;
            case 'displayKey':
                return this.displayKey;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isOptionalDefined && this.optional !== undefined) {
            if (this.optional !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('optional', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.optional);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('optional', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            if (this.displayKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'optional':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.optional = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'optional' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'optional'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.visible = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'visible' cannot be null`);
                    this.visible = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.enabled = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'enabled' cannot be null`);
                    this.enabled = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'displayKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayKey' cannot be null`);
                    this.displayKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayKey'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // optional
        if (this.isOptionalDefined && this.optional !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.optional);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.visible === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.visible);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.enabled === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.enabled);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayKey
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // optional
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.optional = reader.readBooleanValue();
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.visible = <any>null;
                    break;
                case 1:
                    this.visible = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.enabled = <any>null;
                    break;
                case 1:
                    this.enabled = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayKey = <any>null;
                    break;
                case 1:
                    this.displayKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Answer {
        // Method Implementation: simple
        const schema = AnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: AnswerZodSchemaShape): Answer {
        // Method Implementation: parseZodSchema
        return new Answer({
            name: schema.name,
            optional: schema.optional,
            visible: schema.visible,
            enabled: schema.enabled,
            displayKey: schema.displayKey
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            optional: this.isOptionalDefined ? (this.optional) : undefined,
            visible: this.isVisibleDefined ? ((this.visible === null ? undefined : this.visible)) : undefined,
            enabled: this.isEnabledDefined ? ((this.enabled === null ? undefined : this.enabled)) : undefined,
            displayKey: this.isDisplayKeyDefined ? ((this.displayKey === null ? undefined : this.displayKey)) : undefined,
        };
    }

    public clone(): Answer {
        // Method Implementation: clone
        return new Answer({
            name: this.isNameDefined ? this.name : undefined,
            optional: this.isOptionalDefined ? this.optional : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined
        });
    }
}

export abstract class ALinkedLayout implements ILinkedLayout {
    public static readonly modelName: string = 'LinkedLayout';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ALinkedLayout.modelName;
    }

    private _name: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    private _isNameDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public constructor();
    public constructor(source: ILinkedLayout);
    public constructor(source: Partial<Pick<ALinkedLayout, 'name'>>);
    public constructor(source?: ILinkedLayout | Partial<Pick<ALinkedLayout, 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isLinkedLayoutModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ALinkedLayout.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ALinkedLayout {
        // Method Implementation: simple
        const schema = LinkedLayoutZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: LinkedLayoutZodSchemaShape): ALinkedLayout {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.linkedLayoutType)
        {
            case 'inlineLinkedLayout':
            case 'inline':
                return InlineLinkedLayout.parse(schema);
            case 'referenceLinkedLayout':
            case 'reference':
                return ReferenceLinkedLayout.parse(schema);
            default:
                throw new Error(`linkedLayoutType of ${ schema.linkedLayoutType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
        };
    }

    public abstract clone(): ALinkedLayout;
}

export class InlineLinkedLayout extends ALinkedLayout implements IInlineLinkedLayout {
    public static readonly modelName: string = 'InlineLinkedLayout';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'layout' ];

    public get modelName(): string {
        // Method Implementation: simple
        return InlineLinkedLayout.modelName;
    }

    private _layout: Layout;

    public get layout(): Layout {
        // Method Implementation: simple
        return this._layout;
    }

    public set layout(value: Layout) {
        // Method Implementation: simple
        this._layout = value;
        this._isLayoutDefined = true;
    }

    private _isLayoutDefined: boolean;

    public get isLayoutDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutDefined;
    }

    public constructor();
    public constructor(source: IInlineLinkedLayout);
    public constructor(source: Partial<Pick<InlineLinkedLayout, 'layout' | 'name'>>);
    public constructor(source?: IInlineLinkedLayout | Partial<Pick<InlineLinkedLayout, 'layout' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isInlineLinkedLayoutModel(source)) {
                if (source.isLayoutDefined) {
                    this._layout = source.layout;
                    this._isLayoutDefined = true;
                }
                else {
                    this._layout = new Layout();
                    this._isLayoutDefined = true;
                }
            }
            else {
                if (source.layout !== undefined) {
                    this._layout = source.layout;
                    this._isLayoutDefined = true;
                }
                else {
                    this._layout = new Layout();
                    this._isLayoutDefined = true;
                }
            }
        }
        else {
            super();
        
            this._layout = new Layout();
            this._isLayoutDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...InlineLinkedLayout.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isLayoutDefined && this.layout !== undefined)
            definedProperties.push('layout');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'layout':
                return this.layout;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isLayoutDefined && this.layout !== undefined) {
            if (this.layout !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layout', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.layout.getModelName(), this.layout.getDefinedPropertyNames().length));
                this.layout.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layout', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'layout':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === Layout.modelName) {
                        this.layout = new Layout();
                        this.layout.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Layout.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'layout' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layout'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // layout
        if (this.isLayoutDefined && this.layout !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.layout === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.layout.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // layout
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.layout = new Layout();
                    this.layout.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): InlineLinkedLayout {
        // Method Implementation: simple
        const schema = InlineLinkedLayoutZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: InlineLinkedLayoutZodSchemaShape): InlineLinkedLayout {
        // Method Implementation: parseZodSchema
        return new InlineLinkedLayout({
            name: schema.name,
            layout: schema.layout && Layout.parseZodSchema(schema.layout)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            linkedLayoutType: 'inline',
            ...super.toJSON(),
            layout: this.isLayoutDefined ? (this.layout) : undefined,
        };
    }

    public clone(): InlineLinkedLayout {
        // Method Implementation: clone
        return new InlineLinkedLayout({
            name: this.isNameDefined ? this.name : undefined,
            layout: this.isLayoutDefined ? this.layout.clone() : undefined
        });
    }
}

export class ReferenceLinkedLayout extends ALinkedLayout implements IReferenceLinkedLayout {
    public static readonly modelName: string = 'ReferenceLinkedLayout';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'layout' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ReferenceLinkedLayout.modelName;
    }

    private _layout: string;

    public get layout(): string {
        // Method Implementation: simple
        return this._layout;
    }

    public set layout(value: string) {
        // Method Implementation: simple
        this._layout = value;
        this._isLayoutDefined = true;
    }

    private _isLayoutDefined: boolean;

    public get isLayoutDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutDefined;
    }

    public constructor();
    public constructor(source: IReferenceLinkedLayout);
    public constructor(source: Partial<Pick<ReferenceLinkedLayout, 'layout' | 'name'>>);
    public constructor(source?: IReferenceLinkedLayout | Partial<Pick<ReferenceLinkedLayout, 'layout' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isReferenceLinkedLayoutModel(source)) {
                if (source.isLayoutDefined) {
                    this._layout = source.layout;
                    this._isLayoutDefined = true;
                }
                else {
                    this._layout = '';
                    this._isLayoutDefined = false;
                }
            }
            else {
                if (source.layout !== undefined) {
                    this._layout = source.layout;
                    this._isLayoutDefined = true;
                }
                else {
                    this._layout = '';
                    this._isLayoutDefined = false;
                }
            }
        }
        else {
            super();
        
            this._layout = '';
            this._isLayoutDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...ReferenceLinkedLayout.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isLayoutDefined && this.layout !== undefined)
            definedProperties.push('layout');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'layout':
                return this.layout;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isLayoutDefined && this.layout !== undefined) {
            if (this.layout !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layout', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.layout);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layout', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'layout':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.layout = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'layout' cannot be null`);
                    this.layout = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layout'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // layout
        if (this.isLayoutDefined && this.layout !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.layout === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.layout);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // layout
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.layout = <any>null;
                    break;
                case 1:
                    this.layout = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ReferenceLinkedLayout {
        // Method Implementation: simple
        const schema = ReferenceLinkedLayoutZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ReferenceLinkedLayoutZodSchemaShape): ReferenceLinkedLayout {
        // Method Implementation: parseZodSchema
        return new ReferenceLinkedLayout({
            name: schema.name,
            layout: schema.layout
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            linkedLayoutType: 'reference',
            ...super.toJSON(),
            layout: this.isLayoutDefined ? ((this.layout === null ? undefined : this.layout)) : undefined,
        };
    }

    public clone(): ReferenceLinkedLayout {
        // Method Implementation: clone
        return new ReferenceLinkedLayout({
            name: this.isNameDefined ? this.name : undefined,
            layout: this.isLayoutDefined ? this.layout : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Layout':
                return new Layout();
            case 'Group':
                return new Group();
            case 'Question':
                return new Question();
            case 'Answer':
                return new Answer();
            case 'InlineLinkedLayout':
                return new InlineLinkedLayout();
            case 'ReferenceLinkedLayout':
                return new ReferenceLinkedLayout();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Layout':
                return new Layout(source);
            case 'Group':
                return new Group(source);
            case 'Question':
                return new Question(source);
            case 'Answer':
                return new Answer(source);
            case 'InlineLinkedLayout':
                return new InlineLinkedLayout(source);
            case 'ReferenceLinkedLayout':
                return new ReferenceLinkedLayout(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Layout':
                return Layout.parse(source);
            case 'Group':
                return Group.parse(source);
            case 'Question':
                return Question.parse(source);
            case 'Answer':
                return Answer.parse(source);
            case 'InlineLinkedLayout':
                return InlineLinkedLayout.parse(source);
            case 'ReferenceLinkedLayout':
                return ReferenceLinkedLayout.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
